import { createReducer, on } from '@ngrx/store';
import {
  deleteProgramSuccess,
  readProgram,
  readProgramFailure,
  readPrograms,
  readProgramsFailure,
  readProgramsSuccess,
  readProgramSuccess,
  restoreProgramSuccess,
  updateProgramSuccess,
} from './program.actions';
import {
  createProgramSessionSuccess,
  deleteProgramSessionSuccess,
  restoreProgramSessionSuccess,
  updateProgramSessionSuccess,
} from './session/session.actions';
import { ProgramDto, ProgramSessionDto } from '@shared/dto';
import { upsertInArray } from '@shared/utils/array.utils';
import { logout } from '@auth/store/auth.actions';
import { State } from '@store/state.interface';

export const PROGRAM_FEATURE_KEY = 'programs';

export interface ProgramState extends State<ProgramDto> {}

export const initialState: ProgramState = {
  data: null,
  detail: null,
  total: 0,
  query: null,
  loading: false,
  error: null,
};

/** Upsert a program-session into a program's `sessions`, keyed by session id. */
const upsertSession = (program: ProgramDto, session: ProgramSessionDto): ProgramDto => ({
  ...program,
  sessions: upsertInArray(program.sessions ?? [], session) as ProgramSessionDto[],
});

/** Merge a partial program-session update into a program's `sessions`, keyed by session id. */
const patchSession = (program: ProgramDto, session: Partial<ProgramSessionDto>): ProgramDto => {
  const idx = program.sessions?.findIndex(s => s.id === (session as ProgramSessionDto).id) ?? -1;
  if (idx === -1) return program;
  const sessions = [...program.sessions];
  sessions[idx] = { ...sessions[idx], ...session };
  return { ...program, sessions };
};

export const programReducer = createReducer(
  initialState,
  on(readPrograms, (state, { query }) => ({
    ...state,
    query: query ?? state.query,
    loading: true,
    error: null,
  })),
  on(readProgramsSuccess, (state, { data, total }) => ({ ...state, data, total, loading: false })),
  on(readProgramsFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Detail (single entity): the resolver blocks activation and matches by id, so we
  // keep the previous detail during the load (no blank flash on the outgoing page).
  on(readProgram, (state) => ({ ...state, loading: true, error: null })),
  on(readProgramSuccess, (state, { program }) => ({ ...state, detail: program, loading: false })),
  on(readProgramFailure, (state, { error }) => ({ ...state, loading: false, error })),
  // Keep the open detail in sync with mutations so the form/restore-banner stay
  // correct without a refetch (the list itself is refetched by reloadAfterMutation$).
  on(updateProgramSuccess, (state, { program }) => ({
    ...state,
    detail: state.detail ? { ...state.detail, ...program } : state.detail,
  })),
  on(deleteProgramSuccess, (state, { program }) => ({ ...state, detail: program })),
  on(restoreProgramSuccess, (state, { program }) => ({ ...state, detail: program })),
  // Program-session mutations sync both the list cache (`data[].sessions`) and the
  // open detail (`detail.sessions`) so the detail page stays correct without a refetch.
  on(createProgramSessionSuccess, (state, { session }) => ({
    ...state,
    data: state.data?.map(p => (p.id === session.program.id ? upsertSession(p, session) : p)) ?? null,
    detail: state.detail?.id === session.program.id ? upsertSession(state.detail, session) : state.detail,
  })),
  on(updateProgramSessionSuccess, (state, { session }) => ({
    ...state,
    data: state.data?.map(p => patchSession(p, session)) ?? null,
    detail: state.detail ? patchSession(state.detail, session) : state.detail,
  })),
  on(deleteProgramSessionSuccess, (state, { session }) => ({
    ...state,
    data: state.data?.map(p => (p.id === session.program.id ? upsertSession(p, session) : p)) ?? null,
    detail: state.detail?.id === session.program.id ? upsertSession(state.detail, session) : state.detail,
  })),
  on(restoreProgramSessionSuccess, (state, { session }) => ({
    ...state,
    data: state.data?.map(p => (p.id === session.program.id ? upsertSession(p, session) : p)) ?? null,
    detail: state.detail?.id === session.program.id ? upsertSession(state.detail, session) : state.detail,
  })),
  on(logout, () => initialState),
);
